import React, { useEffect, useMemo } from 'react'
import { J2Caution } from '@e3dc-react/icons'
import { makeStyles } from 'tss-react/mui'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { format } from '@e3dc-react/shell/Libs/date'
import { checkIsDeadlineApproaching, sortDevicesByDeadline } from '../../utils'
import { useGetDeviceLinks } from '../../hooks'
import { useParams } from 'react-router-dom'
import { BannerCloseTypes, BannerKey, useBannerContext } from 'src/Libs/banners'
import { Banner } from 'src/Components/banner'

const useStyles = makeStyles()(theme => ({
  containerStyle: {
    backgroundColor: theme.palette.warning.light,
  },
  icon: {
    fontSize: theme.spacing(5),
    color: theme.palette.warning.main,
  },
}))

/**
 * This banner is shown in dashboard of an installation if the
 * installation statement is still incomplete and deadline is approaching (or over).
 */
const InstallationStatementDeadlineDashboardBanner: React.FC = () => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const { removeBanner } = useBannerContext()
  const { installationId } = useParams<{ installationId: string }>()
  const { deviceLinks, isLoading } = useGetDeviceLinks(installationId)

  const incompleteDevicesWithDeadlineApproaching = useMemo(
    () => (deviceLinks ?? []).filter(deviceLink => checkIsDeadlineApproaching(deviceLink)),
    [deviceLinks]
  )

  useEffect(() => {
    if (!isLoading && !incompleteDevicesWithDeadlineApproaching.length) {
      // Remove banner from the queue, but not hide it in DOM! Otherwise the user can't access the next banner in the queue.
      removeBanner(BannerKey.InstallationStatementDeadlineDashboardBanner)
    }
  }, [incompleteDevicesWithDeadlineApproaching, isLoading, removeBanner])

  if (isLoading || !incompleteDevicesWithDeadlineApproaching.length) return <></>

  /* Display the banner with the nearest upcoming installation statement deadline */
  const sortedIbnDeadlines = sortDevicesByDeadline(incompleteDevicesWithDeadlineApproaching)
  const nearestIbnDeadline =
    sortedIbnDeadlines.length && sortedIbnDeadlines[0].ibnDeadlineAt
      ? format(new Date(sortedIbnDeadlines[0].ibnDeadlineAt), t('common.format.date-fns-date'))
      : null

  const bannerI18nKey =
    sortedIbnDeadlines.length === 1
      ? 'installation.banner.installation-statement-deadline.dashboard.warning-text-single'
      : 'installation.banner.installation-statement-deadline.dashboard.warning-text-multiple'

  return (
    <Banner
      bannerKey={BannerKey.InstallationStatementDeadlineDashboardBanner}
      icon={<J2Caution className={classes.icon} />}
      i18nKey={bannerI18nKey}
      values={{ deadline: nearestIbnDeadline ?? '' }}
      containerStyle={classes.containerStyle}
      closeType={BannerCloseTypes.Temporary}
    />
  )
}

export default InstallationStatementDeadlineDashboardBanner
